import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { Calculator, Plus, CheckCircle2, Trash2, Users } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CalculatorPage = () => {
  const { getAuthHeaders } = useAuth();
  const [materials, setMaterials] = useState([]);
  const [recipes, setRecipes] = useState([
    {
      id: 1,
      agglo_type: 'agglo_10',
      name: 'Agglo de 10',
      cement_type: 'ciment_42_5',
      cement_quantity_kg: '',
      sand_quantity_kg: '',
      dune_sand_quantity_kg: '',
      gravel_quantity_kg: '',
      agglo_count: '',
      unit_cost: 0
    },
    {
      id: 2,      agglo_type: 'agglo_15',
      name: 'Agglo de 15',
      cement_type: 'ciment_42_5',
      cement_quantity_kg: '',
      sand_quantity_kg: '',
      dune_sand_quantity_kg: '',
      gravel_quantity_kg: '',
      agglo_count: '',
      unit_cost: 0
    },
    {
      id: 3,
      agglo_type: 'agglo_20',
      name: 'Agglo de 20',
      cement_type: 'ciment_42_5',
      cement_quantity_kg: '',
      sand_quantity_kg: '',
      dune_sand_quantity_kg: '',
      gravel_quantity_kg: '',
      agglo_count: '',
      unit_cost: 0
    },
    {
      id: 4,
      agglo_type: 'agglo_hourdis_16',
      name: 'Agglo Hourdis 16',
      cement_type: 'ciment_42_5',
      cement_quantity_kg: '',
      sand_quantity_kg: '',
      dune_sand_quantity_kg: '',
      gravel_quantity_kg: '',
      agglo_count: '',
      unit_cost: 0
    },
    {
      id: 5,
      agglo_type: 'agglo_plein_15',
      name: 'Agglo Plein 15',
      cement_type: 'ciment_42_5',
      cement_quantity_kg: '',
      sand_quantity_kg: '',
      dune_sand_quantity_kg: '',
      gravel_quantity_kg: '',
      agglo_count: '',
      unit_cost: 0
    }
  ]);
  const [nextId, setNextId] = useState(6);

  useEffect(() => {
    fetchMaterials();
  }, []);

  const fetchMaterials = async () => {
    try {
      const response = await axios.get(`${API}/raw-materials`, getAuthHeaders());
      setMaterials(response.data);
    } catch (error) {
      console.error('Error fetching materials:', error);
      toast.error('Erreur lors du chargement des matières');
    }
  };

  const getMaterialPrice = (type) => {
    const material = materials.find(m => m.type === type || m.name.toLowerCase().includes(type));
    if (!material) return 0;
    return material.price_per_unit / (material.unit === 'tonne' ? 1000 : 1);
  };

  const calculateUnitCost = (recipe) => {
    const cementPrice = getMaterialPrice(recipe.cement_type);
    const sandPrice = getMaterialPrice('sable_0_8');
    const duneSandPrice = getMaterialPrice('gravier_2_4');
    const gravelPrice = getMaterialPrice('gravier_3_8');

    const totalCost = 
      (parseFloat(recipe.cement_quantity_kg) || 0) * cementPrice +
      (parseFloat(recipe.sand_quantity_kg) || 0) * sandPrice +
      (parseFloat(recipe.dune_sand_quantity_kg) || 0) * duneSandPrice +
      (parseFloat(recipe.gravel_quantity_kg) || 0) * gravelPrice;

    const aggloCount = parseFloat(recipe.agglo_count) || 0;
    return aggloCount > 0 ? totalCost / aggloCount : 0;
  };

  const handleRecipeChange = (id, field, value) => {
    setRecipes(recipes.map(recipe => {
      if (recipe.id === id) {
        const updatedRecipe = { ...recipe, [field]: value };
        updatedRecipe.unit_cost = calculateUnitCost(updatedRecipe);
        return updatedRecipe;
      }
      return recipe;
    }));
  };

  const addNewRecipe = () => {
    setRecipes([...recipes, {
      id: nextId,
      agglo_type: 'agglo_10',
      name: `Nouvelle Recette ${nextId}`,
      cement_type: 'ciment_42_5',
      cement_quantity_kg: '',
      sand_quantity_kg: '',
      dune_sand_quantity_kg: '',
      gravel_quantity_kg: '',
      agglo_count: '',
      unit_cost: 0
    }]);
    setNextId(nextId + 1);
  };

  const removeRecipe = (id) => {
    if (recipes.length > 1) {
      setRecipes(recipes.filter(r => r.id !== id));
    } else {
      toast.error('Vous devez garder au moins une recette');
    }
  };

  const validateRecipe = async (recipe) => {
    if (!recipe.cement_quantity_kg || !recipe.agglo_count) {
      toast.error('Veuillez remplir au moins le ciment et le nombre d\'agglos');
      return;
    }

    try {
      // Save the recipe
      await axios.post(
        `${API}/recipes`,
        {
          cement_type: recipe.cement_type,
          cement_quantity_kg: parseFloat(recipe.cement_quantity_kg),
          sand_quantity_kg: parseFloat(recipe.sand_quantity_kg) || 0,
          dune_sand_quantity_kg: parseFloat(recipe.dune_sand_quantity_kg) || 0,
          gravel_quantity_kg: parseFloat(recipe.gravel_quantity_kg) || 0,
          agglo_type: recipe.agglo_type,
          agglo_count: parseInt(recipe.agglo_count)
        },
        getAuthHeaders()
      );

      // Also create/update the recipe template (reference recipe for quick production)
      const aggloCount = parseInt(recipe.agglo_count);
      await axios.post(
        `${API}/recipe-templates`,
        {
          agglo_type: recipe.agglo_type,
          name: recipe.name,
          cement_type: recipe.cement_type,
          cement_per_unit: parseFloat(recipe.cement_quantity_kg) / aggloCount,
          sand_per_unit: (parseFloat(recipe.sand_quantity_kg) || 0) / aggloCount,
          dune_sand_per_unit: (parseFloat(recipe.dune_sand_quantity_kg) || 0) / aggloCount,
          gravel_per_unit: (parseFloat(recipe.gravel_quantity_kg) || 0) / aggloCount
        },
        getAuthHeaders()
      );

      toast.success(`Recette ${recipe.name} validée et enregistrée comme référence`);
      // Reset this recipe
      handleRecipeChange(recipe.id, 'cement_quantity_kg', '');
      handleRecipeChange(recipe.id, 'sand_quantity_kg', '');
      handleRecipeChange(recipe.id, 'dune_sand_quantity_kg', '');
      handleRecipeChange(recipe.id, 'gravel_quantity_kg', '');
      handleRecipeChange(recipe.id, 'agglo_count', '');
    } catch (error) {
      console.error('Error saving recipe:', error);
      toast.error('Erreur lors de l\'enregistrement');
    }
  };

  return (
    <div className="space-y-6" data-testid="calculator-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-4xl font-bold text-[#1B3268] tracking-tight flex items-center gap-3" style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}>
            <Calculator className="w-10 h-10" />
            COUT MP AGGLO
          </h1>
          <p className="text-sm text-slate-600 mt-1">Calculez le coût des matières premières par agglo (la main d'œuvre est suivie dans l'onglet RH / Paie)</p>
        </div>
        <Button
          onClick={addNewRecipe}
          className="bg-[#FDC800] hover:bg-[#e6b600] text-[#1B3268] font-bold rounded-sm shadow-sm"
          data-testid="add-recipe-button"
        >
          <Plus className="w-4 h-4 mr-2" />
          Ajouter Recette
        </Button>
      </div>

      {/* Recipes Grid */}
      <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-6">
        {recipes.map((recipe) => (
          <Card key={recipe.id} className="bg-white border-2 border-[#1B3268] rounded-sm shadow-lg" data-testid={`recipe-card-${recipe.id}`}>
            <div className="border-b-2 border-[#1B3268] p-4 bg-gradient-to-r from-[#1B3268] to-[#2a4785] flex items-center justify-between">
              <h3 className="text-xl font-bold text-white">{recipe.name}</h3>
              {recipes.length > 1 && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => removeRecipe(recipe.id)}
                  className="text-white hover:bg-white/10"
                  data-testid={`remove-recipe-${recipe.id}`}
                >
                  <Trash2 className="w-4 h-4" />
                </Button>
              )}
            </div>
            
            <div className="p-6 space-y-4">
              {/* Type de Ciment */}
              <div>
                <Label className="text-sm font-semibold text-[#1B3268]">Type de Ciment</Label>
                <Select 
                  value={recipe.cement_type} 
                  onValueChange={(value) => handleRecipeChange(recipe.id, 'cement_type', value)}
                >
                  <SelectTrigger className="h-10 border-slate-300 rounded-sm mt-1" data-testid={`cement-type-select-${recipe.id}`}>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    <SelectItem value="ciment_42_5">Ciment 42.5</SelectItem>
                  </SelectContent>
                </Select>
              </div>

              {/* Ciment */}
              <div>
                <Label className="text-sm font-semibold text-[#1B3268]">Ciment (kg)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={recipe.cement_quantity_kg}
                  onChange={(e) => handleRecipeChange(recipe.id, 'cement_quantity_kg', e.target.value)}
                  className="h-10 border-slate-300 rounded-sm mt-1"
                  placeholder="350"
                  data-testid={`cement-input-${recipe.id}`}
                />
              </div>

              {/* Sable */}
              <div>
                <Label className="text-sm font-semibold text-[#1B3268]">Sable (kg)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={recipe.sand_quantity_kg}
                  onChange={(e) => handleRecipeChange(recipe.id, 'sand_quantity_kg', e.target.value)}
                  className="h-10 border-slate-300 rounded-sm mt-1"
                  placeholder="800"
                  data-testid={`sand-input-${recipe.id}`}
                />
              </div>

              {/* Gravier 2/4 */}
              <div>
                <Label className="text-sm font-semibold text-[#1B3268]">Gravier 2/4 (kg)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={recipe.dune_sand_quantity_kg}
                  onChange={(e) => handleRecipeChange(recipe.id, 'dune_sand_quantity_kg', e.target.value)}
                  className="h-10 border-slate-300 rounded-sm mt-1"
                  placeholder="400"
                  data-testid={`dune-sand-input-${recipe.id}`}
                />
              </div>

              {/* Gravier 3/8 */}
              <div>
                <Label className="text-sm font-semibold text-[#1B3268]">Gravier 3/8 (kg)</Label>
                <Input
                  type="number"
                  step="0.01"
                  value={recipe.gravel_quantity_kg}
                  onChange={(e) => handleRecipeChange(recipe.id, 'gravel_quantity_kg', e.target.value)}
                  className="h-10 border-slate-300 rounded-sm mt-1"
                  placeholder="1200"
                  data-testid={`gravel-input-${recipe.id}`}
                />
              </div>

              {/* Nombre d'agglos par gâche */}
              <div>
                <Label className="text-sm font-semibold text-[#1B3268]">Nombre d'agglos / gâche</Label>
                <Input
                  type="number"
                  value={recipe.agglo_count}
                  onChange={(e) => handleRecipeChange(recipe.id, 'agglo_count', e.target.value)}
                  className="h-10 border-slate-300 rounded-sm mt-1"
                  placeholder="100"
                  data-testid={`agglo-count-input-${recipe.id}`}
                />
              </div>

              {/* Prix unitaire MP */}
              <div className="bg-[#FDC800] p-4 rounded-sm">
                <p className="text-xs text-[#1B3268]/70 uppercase tracking-wider mb-1 font-bold">Coût MP / Agglo</p>
                <p className="text-3xl font-bold text-[#1B3268]" data-testid={`unit-cost-${recipe.id}`}>
                  {recipe.unit_cost.toLocaleString('fr-FR', { minimumFractionDigits: 0, maximumFractionDigits: 2 })} FCFA
                </p>
                <p className="text-xs text-[#1B3268]/60 mt-1">Main d'œuvre non incluse (voir RH / Paie)</p>
              </div>

              {/* Valider la recette */}
              <Button
                onClick={() => validateRecipe(recipe)}
                className="w-full bg-[#1B3268] hover:bg-[#152a55] text-white font-bold rounded-sm h-11"
                data-testid={`validate-recipe-${recipe.id}`}
              >
                <CheckCircle2 className="w-4 h-4 mr-2" />
                Valider Recette
              </Button>
            </div>
          </Card>
        ))}
      </div>
    </div>
  );
};

export default CalculatorPage;
